import { Link } from 'react-router-dom'
import type { GovernancePulse as GovernancePulseData, GovernanceStageKey } from './governance-api'

const stages: { key: GovernanceStageKey; label: string; help: string }[] = [
  { key: 'register', label: 'Register', help: 'Identity or accountability gaps' },
  { key: 'assess', label: 'Assess', help: 'Formal Assessment attention' },
  { key: 'control', label: 'Control', help: 'Required Controls outstanding' },
  { key: 'approve', label: 'Approve', help: 'Approval attention' },
  { key: 'monitor', label: 'Monitor', help: 'Monitoring obligations' },
  { key: 'review', label: 'Review', help: 'Periodic Review attention' },
]

export function GovernancePulse({ pulse, loading = false }: { pulse?: GovernancePulseData; loading?: boolean }) {
  const stageAttentionCount = stages.reduce((total, { key }) => total + (pulse?.attentionCounts[key] ?? 0), 0)
  const visibleUseCount = loading ? '—' : pulse?.visibleAIUseCount ?? 0

  return (
    <section className="panel governance-pulse-panel" aria-labelledby="governance-pulse-heading">
      <div className="section-heading compact governance-pulse-heading">
        <div>
          <p className="eyebrow">Governance Pulse</p>
          <h2 id="governance-pulse-heading">Where governance attention is required</h2>
          <p>Each segment shows how many AI Uses need attention at that governance stage. It is not a maturity, safety or compliance score.</p>
        </div>
        <span className="governance-pulse-key" aria-label="Hot pink marks a stage with attention required">
          <span aria-hidden="true" /> Attention required
        </span>
      </div>

      <div className="governance-pulse-visual" aria-label="Governance Pulse attention counts">
        <div className="governance-pulse-orbit">
          <svg className="governance-pulse-ring" viewBox="0 0 220 220" aria-hidden="true">
            <circle className="governance-pulse-track" cx="110" cy="110" r="84" />
            {stages.map(({ key }, index) => {
              const count = pulse?.attentionCounts[key] ?? 0
              return (
                <circle
                  key={key}
                  className={`governance-pulse-arc governance-pulse-arc-${key} ${count > 0 ? 'has-attention' : 'quiet'}`}
                  cx="110"
                  cy="110"
                  r="84"
                  pathLength="100"
                  strokeDasharray="13.5 86.5"
                  transform={`rotate(${-90 + index * 60} 110 110)`}
                  style={{ animationDelay: `${80 + index * 55}ms` }}
                />
              )
            })}
          </svg>

          <div className="governance-pulse-centre">
            <span>AI uses in view</span>
            <strong>{visibleUseCount}</strong>
            <small>{loading ? 'Checking governance position' : stageAttentionCount === 0 ? 'No stage attention signals' : `${stageAttentionCount} stage attention signal${stageAttentionCount === 1 ? '' : 's'}`}</small>
          </div>

          {stages.map(({ key, label, help }) => {
            const count = pulse?.attentionCounts[key] ?? 0
            return (
              <Link
                key={key}
                className={`governance-pulse-stage governance-pulse-stage-${key}${count > 0 ? ' has-attention' : ''}`}
                to={`/ai-register?governance=${key}`}
                aria-label={`${label}: ${loading ? 'loading' : `${count} AI Uses require attention`}. ${help}. Open the filtered AI Register.`}
              >
                <span className="governance-pulse-stage-count">{loading ? '—' : count}</span>
                <span className="governance-pulse-stage-label">{label}</span>
                <small>{help}</small>
              </Link>
            )
          })}
        </div>
      </div>

      <p className="governance-pulse-footnote">Select a stage to open the AI Register filtered to the uses that need attention there.</p>
    </section>
  )
}
